import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip, Paper, Stack, TextField, Typography, } from "@mui/material";
import React from "react";
import { useFormContext } from "react-hook-form";
const DAY_LABELS = [
    { key: "monday", label: "Monday", short: "Mon" },
    { key: "tuesday", label: "Tuesday", short: "Tue" },
    { key: "wednesday", label: "Wednesday", short: "Wed" },
    { key: "thursday", label: "Thursday", short: "Thu" },
    { key: "friday", label: "Friday", short: "Fri" },
    { key: "saturday", label: "Saturday", short: "Sat" },
    { key: "sunday", label: "Sunday", short: "Sun" },
];
const DEFAULT_DAY_CONFIG = {
    enabled: false,
    timeWindows: [{ startTime: "09:00", endTime: "17:00" }],
};
export const createDefaultDays = () => ({
    monday: { ...DEFAULT_DAY_CONFIG },
    tuesday: { ...DEFAULT_DAY_CONFIG },
    wednesday: { ...DEFAULT_DAY_CONFIG },
    thursday: { ...DEFAULT_DAY_CONFIG },
    friday: { ...DEFAULT_DAY_CONFIG },
    saturday: { ...DEFAULT_DAY_CONFIG },
    sunday: { ...DEFAULT_DAY_CONFIG },
});
// Calculate slots per window based on match timing
const calculateSlotsPerWindow = (window, slotDuration) => {
    if (!window)
        return 0;
    const startParts = window.startTime.split(":").map(Number);
    const endParts = window.endTime.split(":").map(Number);
    const startH = startParts[0] ?? 9;
    const startM = startParts[1] ?? 0;
    const endH = endParts[0] ?? 17;
    const endM = endParts[1] ?? 0;
    const totalMinutes = endH * 60 + endM - (startH * 60 + startM);
    if (totalMinutes <= 0)
        return 0; // End time must be after start time
    return Math.floor(totalMinutes / slotDuration);
};
// Check if a time window is valid (end after start)
const isTimeWindowValid = (window) => {
    if (!window)
        return true;
    const startParts = window.startTime.split(":").map(Number);
    const endParts = window.endTime.split(":").map(Number);
    const startMinutes = (startParts[0] ?? 0) * 60 + (startParts[1] ?? 0);
    const endMinutes = (endParts[0] ?? 0) * 60 + (endParts[1] ?? 0);
    return endMinutes > startMinutes;
};
// Calculate total slots per week for a pitch
const calculatePitchSlotsPerWeek = (schedule, slotDuration) => {
    return DAY_LABELS.reduce((sum, d) => {
        const dayConfig = schedule.days[d.key];
        if (!dayConfig.enabled || dayConfig.timeWindows.length === 0)
            return sum;
        return (sum + calculateSlotsPerWindow(dayConfig.timeWindows[0], slotDuration));
    }, 0);
};
export const ScheduleWindowStep = ({ context, }) => {
    const { pitches, gamesPerRound, totalRounds } = context;
    const { watch, setValue } = useFormContext();
    const matchTiming = watch("matchTiming");
    const scheduleWindowValue = watch("scheduleWindow");
    const defaultSlotDuration = matchTiming
        ? matchTiming.matchDuration +
            matchTiming.halfTimeBreak +
            matchTiming.warmupTime
        : 45;
    // Build pitchSchedules from form state, syncing with current pitches
    const pitchSchedules = React.useMemo(() => {
        const existingSchedules = scheduleWindowValue?.pitchSchedules ?? [];
        const existingMap = new Map(existingSchedules.map((s) => [s.pitchId, s]));
        if (pitches.length === 0) {
            // No pitches - use "All Venues" mode
            const existing = existingMap.get(0);
            return [
                {
                    pitchId: 0,
                    pitchName: "All Venues",
                    venueId: 0,
                    venueName: "",
                    days: existing?.days ?? createDefaultDays(),
                },
            ];
        }
        // Map pitches to schedules, preserving existing day configs
        return pitches.map((p) => {
            const existing = existingMap.get(p.id);
            return {
                pitchId: p.id,
                pitchName: p.name,
                venueId: p.venueId,
                venueName: p.venueName,
                days: existing?.days ?? createDefaultDays(),
            };
        });
    }, [scheduleWindowValue?.pitchSchedules, pitches]);
    // Helper to update schedules via form context
    const updateSchedules = React.useCallback((newSchedules) => {
        setValue("scheduleWindow", { pitchSchedules: newSchedules });
    }, [setValue]);
    const handleDayToggle = (pitchIndex, dayKey) => {
        const schedule = pitchSchedules[pitchIndex];
        if (!schedule)
            return;
        const current = schedule.days[dayKey];
        const updated = [...pitchSchedules];
        updated[pitchIndex] = {
            ...schedule,
            days: {
                ...schedule.days,
                [dayKey]: {
                    ...current,
                    enabled: !current.enabled,
                    timeWindows: !current.enabled
                        ? [{ startTime: "09:00", endTime: "17:00" }]
                        : current.timeWindows,
                },
            },
        };
        updateSchedules(updated);
    };
    const handleTimeChange = (pitchIndex, dayKey, field, newValue) => {
        const schedule = pitchSchedules[pitchIndex];
        if (!schedule)
            return;
        const current = schedule.days[dayKey];
        const existingWindow = current.timeWindows[0] || {
            startTime: "09:00",
            endTime: "17:00",
        };
        const updated = [...pitchSchedules];
        updated[pitchIndex] = {
            ...schedule,
            days: {
                ...schedule.days,
                [dayKey]: {
                    ...current,
                    timeWindows: [
                        {
                            startTime: field === "startTime" ? newValue : existingWindow.startTime,
                            endTime: field === "endTime" ? newValue : existingWindow.endTime,
                        },
                    ],
                },
            },
        };
        updateSchedules(updated);
    };
    const totalSlotsPerWeek = pitchSchedules.reduce((sum, schedule) => sum + calculatePitchSlotsPerWeek(schedule, defaultSlotDuration), 0);
    const totalGamesNeeded = gamesPerRound * totalRounds;
    const estimatedWeeksNeeded = totalSlotsPerWeek > 0 ? Math.ceil(totalGamesNeeded / totalSlotsPerWeek) : 0;
    return (_jsxs(Box, { children: [_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 3 }, children: "Define which days and times matches can be scheduled at each pitch/venue." }), gamesPerRound > 0 && totalSlotsPerWeek > 0 && (_jsxs(Alert, { severity: "info", sx: { mb: 3 }, children: ["Estimated ", estimatedWeeksNeeded, " weeks to complete ", totalRounds, " ", "rounds (", totalGamesNeeded, " games) at ", totalSlotsPerWeek, " slots/week"] })), _jsxs(Box, { sx: { mb: 3 }, children: [_jsx(Typography, { variant: "subtitle2", sx: { mb: 2 }, children: "Pitch Availability" }), pitchSchedules.map((schedule, pitchIndex) => {
                        const enabledDays = DAY_LABELS.filter((d) => schedule.days[d.key].enabled);
                        const slotsPerWeek = calculatePitchSlotsPerWeek(schedule, defaultSlotDuration);
                        return (_jsxs(Accordion, { defaultExpanded: true, sx: { mb: 1 }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, {}), children: _jsxs(Box, { sx: {
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            pr: 2,
                                        }, children: [_jsx(Box, { children: _jsx(Typography, { variant: "subtitle2", children: schedule.venueName
                                                        ? `${schedule.venueName} → ${schedule.pitchName}`
                                                        : schedule.pitchName }) }), _jsx(Chip, { size: "small", label: `${slotsPerWeek} slots/week`, color: slotsPerWeek > 0 ? "primary" : "default", variant: "outlined" })] }) }), _jsxs(AccordionDetails, { children: [_jsx(Box, { sx: { mb: 2 }, children: _jsxs(Typography, { variant: "caption", color: "text.secondary", children: ["Slot Duration: ", _jsxs("strong", { children: [defaultSlotDuration, " min"] }), " ", "(from match timing)"] }) }), _jsx(Stack, { direction: "row", spacing: 1, sx: { mb: 2, flexWrap: "wrap", gap: 1 }, children: DAY_LABELS.map((day) => (_jsx(Chip, { label: day.short, color: schedule.days[day.key].enabled ? "primary" : "default", variant: schedule.days[day.key].enabled ? "filled" : "outlined", onClick: () => handleDayToggle(pitchIndex, day.key), size: "small" }, day.key))) }), enabledDays.length > 0 && (_jsx(Stack, { spacing: 1, children: enabledDays.map((day) => {
                                                const timeWindow = schedule.days[day.key].timeWindows[0];
                                                const isValid = isTimeWindowValid(timeWindow);
                                                return (_jsxs(Box, { sx: {
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 2,
                                                        flexWrap: "wrap",
                                                    }, children: [_jsx(Typography, { variant: "body2", sx: { minWidth: 70, fontWeight: 500 }, children: day.short }), _jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", useFlexGap: true, flexWrap: "wrap", sx: { flex: { xs: "1 1 100%", sm: "0 0 auto" } }, children: [_jsx(TextField, { type: "time", size: "small", value: timeWindow?.startTime || "09:00", onChange: (e) => handleTimeChange(pitchIndex, day.key, "startTime", e.target.value), error: !isValid, sx: {
                                                                        width: { xs: "auto", sm: 140 },
                                                                        flex: { xs: "1 1 110px", sm: "none" },
                                                                    }, inputProps: { step: 900 } }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "\u2013" }), _jsx(TextField, { type: "time", size: "small", value: timeWindow?.endTime || "17:00", onChange: (e) => handleTimeChange(pitchIndex, day.key, "endTime", e.target.value), error: !isValid, sx: {
                                                                        width: { xs: "auto", sm: 140 },
                                                                        flex: { xs: "1 1 110px", sm: "none" },
                                                                    }, inputProps: { step: 900 } }), isValid ? (_jsx(Chip, { size: "small", label: `${calculateSlotsPerWindow(timeWindow, defaultSlotDuration)}`, variant: "outlined", sx: { minWidth: 30 } })) : (_jsx(Typography, { variant: "caption", color: "error", sx: { whiteSpace: "nowrap" }, children: "End must be after start" }))] })] }, day.key));
                                            }) })), enabledDays.length === 0 && (_jsx(Typography, { variant: "body2", color: "text.secondary", children: "Select days when this pitch is available" }))] })] }, schedule.pitchId));
                    })] }), _jsxs(Paper, { variant: "outlined", sx: {
                    p: 3,
                    bgcolor: "action.hover",
                    borderRadius: 2,
                }, children: [_jsx(Typography, { variant: "subtitle2", color: "text.secondary", sx: { mb: 2, textTransform: "uppercase", letterSpacing: 0.5 }, children: "Schedule Summary" }), _jsxs(Box, { sx: {
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                            gap: 2,
                        }, children: [_jsxs(Box, { children: [_jsx(Stack, { direction: "row", spacing: 1, alignItems: "center", children: _jsx(Typography, { variant: "h4", color: "primary.main", children: pitchSchedules.length }) }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: pitchSchedules.length === 1 ? "pitch" : "pitches" })] }), _jsxs(Box, { children: [_jsx(Typography, { variant: "h4", color: "text.secondary", children: totalSlotsPerWeek }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "slots/week total" })] }), _jsxs(Box, { children: [_jsx(Typography, { variant: "h4", color: "text.secondary", children: gamesPerRound }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "games/round" })] }), _jsxs(Box, { children: [_jsx(Typography, { variant: "h4", color: "text.secondary", children: totalGamesNeeded }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "total games" })] })] }), gamesPerRound > 0 && totalSlotsPerWeek > 0 && (_jsx(_Fragment, { children: totalSlotsPerWeek >= gamesPerRound ? (_jsx(Alert, { severity: "success", icon: _jsx(InfoOutlinedIcon, {}), sx: { mt: 2 }, children: _jsxs(Typography, { variant: "body2", children: ["You can schedule ", _jsx("strong", { children: "1 round per week" }), " with", " ", totalSlotsPerWeek - gamesPerRound, " spare slots."] }) })) : (_jsx(Alert, { severity: "warning", icon: _jsx(ErrorOutlineIcon, {}), sx: { mt: 2 }, children: _jsxs(Typography, { variant: "body2", children: ["You need ", _jsxs("strong", { children: [gamesPerRound, " slots/week"] }), " for 1 round but only have ", totalSlotsPerWeek, ". Consider adding more availability or playing multiple rounds per week may take longer."] }) })) })), totalSlotsPerWeek === 0 && (_jsx(Alert, { severity: "warning", sx: { mt: 2 }, children: _jsx(Typography, { variant: "body2", children: "Select at least one day on at least one pitch to schedule matches." }) }))] })] }));
};
